'use client'

import { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { ArrowLeft, Settings } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ChannelIcon } from '@/components/ui/channel-icon'
import { AgentStatusBadge } from '../agent-status-badge'
import { AiReplyOffBadge } from '../ai-reply-off-badge'
import { sortChannelsForDisplay } from '../constants'
import { useMessagingAgents, type MessageAgentWithChannels } from '@/hooks/agents/messaging/use-agents'
import { useConversations, type ConversationFilters } from '@/hooks/agents/messaging/use-conversations'
import { useMessages } from '@/hooks/agents/messaging/use-messages'
import {
  useExcludeConversationContactMutation,
  useIncludeConversationContactMutation,
} from '@/hooks/mutations/message-agent-exclusions'
import type { ContactExclusionsView } from '@/server-actions/message-agent-exclusions'
import { isExcludedContact } from '@/lib/agents/messaging/contact-exclusions'
import { isAiReplyEnabled, type MessageAgentConfig, type MessageConversation } from '@/lib/agents/messaging/types'
import { useInboxSelection } from '@/hooks/use-inbox-selection'
import { idOfScopedValue, scopedId, sourceOfScopedValue } from '@/lib/inbox/scoped-id'
import { InboxHeaderButton, InboxLayout } from '@/components/ui/conversation/inbox-layout'
import { ConversationList } from './conversation-list'
import { MessageThread } from './message-thread'
import { InboxFilters } from './inbox-filters'
import { getChannelExternalUrl } from '../constants'
import { useAuth } from '@/hooks/auth/use-auth'
import { resolveUserTimezone } from '@/lib/date-utils'
import { useTranslations } from "next-intl"



interface ConversationsInboxPageProps {
  agentId: string
  onBack: () => void
}

/** Handover mail, Telegram alerts and older links carry the bare conversation id. */
const LEGACY_KEYS = ["conversationId"] as const

export function ConversationsInboxPage({ agentId, onBack }: ConversationsInboxPageProps) {

  const tUi = useTranslations("Messaging.AgentsMessagingInboxConversationsInboxPage")

  const router = useRouter()
  const { user } = useAuth()
  const timezone = useMemo(() => resolveUserTimezone(user), [user])
  const { agents } = useMessagingAgents()
  const agent = useMemo(() => agents.find((a) => a.id === agentId), [agents, agentId])

  // The agents query is not refetched by the exclusion mutations, so the list they
  // return is the fresher source until it is.
  const [exclusionsOverride, setExclusionsOverride] = useState<ContactExclusionsView | null>(null)

  const [filters, setFilters] = useState<ConversationFilters>({})
  const { selectedId, select } = useInboxSelection({
    legacyKeys: LEGACY_KEYS,
    resolveLegacy: (_key, value) => scopedId("message", value),
  })
  // The URL carries the scoped id; everything below this line speaks the bare one,
  // and a value of another source is not this page's to open.
  const selectedConversationId =
    selectedId && sourceOfScopedValue(selectedId) === "message" ? idOfScopedValue(selectedId) : null

  // Memoize filters to avoid re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally depend on individual filter fields
  const stableFilters = useMemo(() => filters, [filters.status, filters.channel_type, filters.search])

  const {
    conversations,
    isLoading: conversationsLoading,
    total,
    hasMore,
    loadMoreFailed,
    loadMore,
    markAsRead,
    patchPauseState: patchListPauseState,
  } = useConversations(agentId, stableFilters)

  const {
    conversation: activeConversation,
    messages,
    isLoading: messagesLoading,
    hasMore: hasMoreMessages,
    openConversation,
    loadOlder,
    refresh: refreshMessages,
    sendMessage,
    toggleAiPaused,
    toggleAutoPauseExempt,
    startNewSession,
  } = useMessages(agentId, markAsRead)

  // Whether the contact is ON THE LIST — not whether the conversation happens to be
  // paused because of it. An operator can lift that pause and the exclusion stands.
  const contactExcluded = useMemo(() => {
    if (!activeConversation) return false
    const contactExclusions = exclusionsOverride ?? agent?.config?.contact_exclusions
    if (!contactExclusions) return false
    return isExcludedContact({ contact_exclusions: contactExclusions } as MessageAgentConfig, {
      contact_name: activeConversation.contact_name,
      contact_handle: activeConversation.contact_handle,
      channel_type: activeConversation.channel_type as MessageConversation['channel_type'],
      external_user_id: activeConversation.external_user_id,
    })
  }, [activeConversation, exclusionsOverride, agent?.config?.contact_exclusions])

  const connectedChannels = useMemo(() =>
    sortChannelsForDisplay((agent?.channels || []).filter((c) => c.status === 'connected')),
    [agent?.channels]
  )

  const replyingAs = useMemo(() => {
    if (!activeConversation) return null
    const ch = connectedChannels.find((c) => c.channel_type === activeConversation.channel_type)
    if (!ch) return null
    return {
      account_name: ch.account_name,
      profile_picture_url: ch.config?.profile_picture_url as string | undefined,
      channel_url: getChannelExternalUrl(ch),
    }
  }, [activeConversation, connectedChannels])

  // Track all channels ever seen (not just current filtered set — prevents filter hiding itself)
  const seenChannelsRef = useRef(new Set<string>())
  const availableChannels = useMemo(() => {
    for (const c of conversations) seenChannelsRef.current.add(c.channel_type)
    return [...seenChannelsRef.current].sort()
  }, [conversations])

  // Wrap sendMessage to sync ai_paused to conversation list from server response
  const handleSendMessage = useCallback(async (text: string) => {
    const { ai_paused } = await sendMessage(text)
    if (selectedConversationId && ai_paused) {
      patchListPauseState(selectedConversationId, { ai_paused: true })
    }
  }, [sendMessage, selectedConversationId, patchListPauseState])

  // Both toggles hit the same endpoint and can move both flags — mirror the server response
  // into the list rather than the requested value.
  const handleToggleAiPaused = useCallback(async (paused: boolean) => {
    const flags = await toggleAiPaused(paused)
    if (selectedConversationId && flags) {
      patchListPauseState(selectedConversationId, flags)
    }
  }, [toggleAiPaused, selectedConversationId, patchListPauseState])

  const handleToggleAutoPauseExempt = useCallback(async (exempt: boolean) => {
    const flags = await toggleAutoPauseExempt(exempt)
    if (selectedConversationId && flags) {
      patchListPauseState(selectedConversationId, flags)
    }
  }, [toggleAutoPauseExempt, selectedConversationId, patchListPauseState])

  // Inbox exclusion: server action pauses + lists the contact; refresh pulls
  // the new pause origin into the thread banner/badge.
  const { mutateAsync: excludeContact } = useExcludeConversationContactMutation()
  const { mutateAsync: includeContact } = useIncludeConversationContactMutation()
  const handleExcludeContact = useCallback(async () => {
    if (!selectedConversationId) return
    setExclusionsOverride(await excludeContact({ conversationId: selectedConversationId }))
    patchListPauseState(selectedConversationId, { ai_paused: true })
    await refreshMessages()
  }, [excludeContact, selectedConversationId, patchListPauseState, refreshMessages])

  // Inverse action: the server drops every matching entry and lifts the
  // exclusion pause, so the thread must reload its pause origin too.
  const handleIncludeContact = useCallback(async () => {
    if (!selectedConversationId) return
    const result = await includeContact({ conversationId: selectedConversationId })
    setExclusionsOverride(result)
    patchListPauseState(selectedConversationId, { ai_paused: result.ai_paused })
    await refreshMessages()
  }, [includeContact, selectedConversationId, patchListPauseState, refreshMessages])

  const handleSelectConversation = useCallback(
    (conversationId: string) => select(scopedId("message", conversationId)),
    [select],
  )

  // One owner for "a conversation is selected": a row press and a link on mount
  // both land here. openConversation loads it directly, so it need not be in
  // the loaded page; markAsRead clears the badge locally, the server call is
  // fire-and-forget in the hook.
  useEffect(() => {
    if (!selectedConversationId) return
    openConversation(selectedConversationId)
    markAsRead(selectedConversationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per selected conversation; the callbacks are recreated each render
  }, [selectedConversationId])

  const notice = agent ? agentNotice(tUi, agent) : null

  return (
    <InboxLayout
      hasSelection={!!selectedConversationId}
      header={
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2 split:gap-4">
            <InboxHeaderButton
              variant="ghost"
              size="sm"
              className="px-2 split:px-3"
              icon={<ArrowLeft className="h-4 w-4 split:mr-2" />}
              label={tUi("back")}
              onClick={onBack}
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 split:gap-3">
                <h1 className="truncate text-lg font-semibold split:text-2xl">
                  {agent?.name || tUi("conversations")}
                </h1>
                {agent && <AgentStatusBadge status={agent.status} />}
                {agent && !isAiReplyEnabled(agent.config) && <AiReplyOffBadge className="shrink-0" />}
              </div>
              <div className="mt-1 hidden items-center gap-3 split:flex">
                <p className="text-sm text-muted-foreground">
                  {total} <span><span key={tUi("conversation")}>{tUi("conversation")}</span></span><span>{total !== 1 ? 's' : ''}</span>
                </p>
                {connectedChannels.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">·</span>
                    {connectedChannels.map((ch) => (
                      <div key={ch.id} className="flex items-center gap-1.5" title={tUi("replyingAsValue0", { value0: String(ch.account_name || ch.channel_type) })}>
                        {ch.config?.profile_picture_url ? (
                          <Avatar className="h-5 w-5">
                            <AvatarImage src={ch.config.profile_picture_url as string} />
                            <AvatarFallback className="text-[8px]">
                              {(ch.account_name || '?').slice(0, 1).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                        ) : (
                          <ChannelIcon channelType={ch.channel_type} size={14} />
                        )}
                        <span className="text-xs text-muted-foreground">{ch.account_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <InboxHeaderButton
            variant="outline"
            className="shrink-0"
            icon={<Settings className="h-4 w-4 split:mr-2" />}
            label={tUi("settings")}
            onClick={() => router.push(`?page=messaging-agents&agentId=${agentId}`)}
          />
        </div>
      }
      notice={notice && <p className="text-sm text-yellow-700">{notice}</p>}
      list={
        <>
          <InboxFilters filters={filters} onChange={setFilters} availableChannels={availableChannels} />
          <ConversationList
            conversations={conversations}
            activeId={selectedConversationId}
            isLoading={conversationsLoading}
            hasMore={hasMore}
            loadMoreFailed={loadMoreFailed}
            onSelect={handleSelectConversation}
            onLoadMore={loadMore}
            timezone={timezone}
          />
        </>
      }
      pane={
        <MessageThread
          conversation={activeConversation}
          messages={messages}
          isLoading={messagesLoading}
          hasMore={hasMoreMessages}
          replyingAs={replyingAs}
          onLoadOlder={loadOlder}
          onRefresh={refreshMessages}
          onSendMessage={handleSendMessage}
          onToggleAiPaused={handleToggleAiPaused}
          onToggleAutoPauseExempt={handleToggleAutoPauseExempt}
          onNewSession={startNewSession}
          contactExcluded={contactExcluded}
          onExcludeContact={handleExcludeContact}
          onIncludeContact={handleIncludeContact}
          timezone={timezone}
          onBack={() => select(null)}
        />
      }
    />
  )
}

/** What a reply sent from here will and will not do — one line, or nothing for a normal agent. */
function agentNotice(tUi: ReturnType<typeof useTranslations<"Messaging.AgentsMessagingInboxConversationsInboxPage">>, agent: MessageAgentWithChannels): string | null {
  switch (agent.status) {
    case 'draft':
      return tUi("thisAgentIsADraftItNeverAnswers")
    case 'active':
      return isAiReplyEnabled(agent.config)
        ? null
        : tUi("aIReplyIsSwitchedOffForThisAgent")
    case 'disabled':
      return tUi("thisAgentIsDisabledItSendsNoAI")
  }
}
